import type { BoxProps } from '@chakra-ui/react'
import { Box } from '@chakra-ui/react'
import type { FC, PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { useState } from 'react'
import { memo } from 'react'
import { useMemo } from 'react'

import { ChatNavigationContext } from '@app/pages/strategies/components/aiChat/contexts/aiChatNavigationContext'
import List from '@app/pages/strategies/components/aiChat/list'
import New from '@app/pages/strategies/components/aiChat/new'
import Show from '@app/pages/strategies/components/aiChat/show'
import { useStore } from '@app/store'
import { Chat, OrganizationChats } from '@graphql/documents/ai_assistant.graphql'
import type {
  ChatQuery,
  ChatQueryVariables,
  OrganizationChatsQuery,
  OrganizationChatsQueryVariables
} from '@graphql/queries'
import type { ChattableObjectInput, PipelineEnum } from '@graphql/types'

export type EmbeddedChatNavigationHistory = Array<[string] | [string, string]>

type Props = BoxProps & {
  pipelineId: PipelineEnum
  chattableType: keyof ChattableObjectInput
  chattableId: string
  strategyId?: string
}

const EmbeddedAiChat: FC<PropsWithChildren<Props>> = ({
  pipelineId,
  chattableType,
  chattableId,
  strategyId = null,
  ...rest
}) => {
  const [history, setHistory] = useState<EmbeddedChatNavigationHistory>(() => [])
  const loaderQuery = useStore.use.loaderQuery()

  const [action, chatId] = history[history.length - 1] || []

  useEffect(() => {
    loaderQuery<OrganizationChatsQuery, OrganizationChatsQueryVariables>(OrganizationChats, {
      chattableType,
      chattableId
    })
  }, [chattableId, chattableType, loaderQuery, pipelineId, strategyId])

  useEffect(() => {
    if (chatId) {
      loaderQuery<ChatQuery, ChatQueryVariables>(Chat, { id: chatId })
    }
  }, [loaderQuery, chatId])

  const value = useMemo(
    () => ({
      show: (id: string) => {
        setHistory((prev) => [...prev, ['show', id]])
      },
      list: () => {
        setHistory((prev) => [...prev, ['list']])
      },
      new: () => {
        setHistory((prev) => [...prev, ['new']])
      }
    }),
    []
  )

  const rendered = useMemo(() => {
    if (history.length === 0) {
      return <List chattableType={chattableType} chattableId={chattableId} inDrawer={false} />
    }

    if (action === 'show') {
      return (
        <Show
          chatId={chatId}
          inDrawer={false}
          chattableId={chattableId}
          chattableType={chattableType}
          strategyId={strategyId}
        />
      )
    }

    if (action === 'new') {
      return (
        <New
          pipelineId={pipelineId}
          inDrawer={false}
          chattableId={chattableId}
          chattableType={chattableType}
          strategyId={strategyId}
        />
      )
    }

    return <List chattableType={chattableType} chattableId={chattableId} inDrawer={false} />
  }, [action, chatId, chattableId, chattableType, history.length, pipelineId, strategyId])

  return (
    <Box pos="relative" overflow="hidden" {...rest}>
      <ChatNavigationContext.Provider value={value}>{rendered}</ChatNavigationContext.Provider>
    </Box>
  )
}

export default memo(EmbeddedAiChat)
